// /src/shortcut-router.ts
import { TrauvoShortcuts } from './plugins/shortcuts';

/**
 * Map your shortcut types -> routes here.
 * Keep these in sync with Info.plist UIApplicationShortcutItems
 */
const SHORTCUT_ROUTE_MAP: Record<string, string> = {
  'com.trauvo.nearby':    '/nearby',
  'com.trauvo.favorites': '/favorites',
  // add more here:
  // 'com.trauvo.explore': '/explore',
};

function navigate(path: string) {
  // SPA-style hash routing (framework-free)
  // If you prefer full navigations, swap to: window.location.href = path;
  if (!path.startsWith('/')) path = '/' + path;
  window.location.hash = '#' + path;
}

/**
 * Handle a single incoming shortcut type.
 */
function handleShortcutType(type?: string | null) {
  if (!type) return;
  const path = SHORTCUT_ROUTE_MAP[type];
  if (path) navigate(path);
}

/**
 * Initialize once during app bootstrap.
 * - Subscribes to live events (foreground)
 * - Reads any pending shortcut (cold start)
 */
export async function initShortcutBridge() {
  // Foreground events (when app is already open)
  try {
    await TrauvoShortcuts.addListener('shortcut', ({ type }) => handleShortcutType(type));
  } catch {
    // Not native (web) or plugin not available — safe to ignore
  }

  // Cold start (launched from a quick action before JS loaded)
  try {
    const { type } = await TrauvoShortcuts.getPendingShortcut();
    handleShortcutType(type);
  } catch {
    // Web mode / no plugin — ignore
  }
}

/**
 * Optional tiny hash router helper for your HTML sections.
 * Call this once after DOMContentLoaded to show the right section by hash.
 */
export function bindHashViewRenderer(opts?: {
  routes?: Record<string, string>; // hash path -> element id
  containerSelector?: string;      // CSS selector for the container of <section> views
}) {
  const routes = opts?.routes ?? {
    '/nearby':    'view-nearby',
    '/favorites': 'view-favorites',
    '/':          'view-home',
  };
  const containerSel = opts?.containerSelector ?? 'main';

  const render = () => {
    const hash = location.hash.replace(/^#/, '') || '/';
    const id = routes[hash] ?? routes['/'];
    const container = document.querySelector(containerSel);
    if (!container) return;

    const sections = container.querySelectorAll(':scope > section');
    sections.forEach((el) => (el as HTMLElement).hidden = true);

    const target = id ? document.getElementById(id) : null;
    if (target) (target as HTMLElement).hidden = false;
  };

  window.addEventListener('hashchange', render);
  document.addEventListener('DOMContentLoaded', render);
  // Render immediately if hash already present
  render();
}
